/**
 * Color palettes for signature field visualization.
 * Used in both the sidebar panel and PDF overlay components.
 */

import { SignaturesPreviewResponse } from './model';

/** Light colors for signature field backgrounds (30% opacity) */
export const SIGNATURE_ROLE_COLORS_LIGHT: readonly string[] = [
    'rgba(66, 133, 244, 0.30)', // blue
    'rgba(234, 67, 53, 0.30)', // red
    'rgba(52, 168, 83, 0.30)', // green
    'rgba(251, 188, 4, 0.30)', // yellow
    'rgba(171, 71, 188, 0.30)', // purple
    'rgba(255, 112, 67, 0.30)', // orange
    'rgba(0, 172, 193, 0.30)', // teal
    'rgba(141, 110, 99, 0.30)', // brown
    'rgba(233, 30, 99, 0.30)', // pink
    'rgba(63, 81, 181, 0.30)' // indigo
] as const;

/** Dark colors for signature field borders and sidebar indicators (80% opacity) */
export const SIGNATURE_ROLE_COLORS_DARK: readonly string[] = [
    'rgba(66, 133, 244, 0.80)', // blue
    'rgba(234, 67, 53, 0.80)', // red
    'rgba(52, 168, 83, 0.80)', // green
    'rgba(251, 188, 4, 0.80)', // yellow
    'rgba(171, 71, 188, 0.80)', // purple
    'rgba(255, 112, 67, 0.80)', // orange
    'rgba(0, 172, 193, 0.80)', // teal
    'rgba(141, 110, 99, 0.80)', // brown
    'rgba(233, 30, 99, 0.80)', // pink
    'rgba(63, 81, 181, 0.80)' // indigo
] as const;

/** Get the color index for a role, wrapping around if more roles than colors */
export const getSignatureColorIndex = (index: number): number =>
    index % SIGNATURE_ROLE_COLORS_DARK.length;

/** Explicit mapping from DocuSign role names to display names */
const ROLE_DISPLAY_NAMES: Record<string, string> = {
    ApprovalsSignerCustodian1: 'Custodian',
    ApprovalsSignerFundSponsor1: 'Fund Sponsor',
    Advisor: 'Advisor',
    AdditionalAdvisor1: 'Additional Advisor 1',
    AdditionalAdvisor2: 'Additional Advisor 2',
    MarketplacePrincipal: 'Principal',
    Investor: 'Investor',
    JointInvestor: 'Joint Investor',
    AuthorizedPerson3: 'Authorized Person 3',
    AuthorizedPerson4: 'Authorized Person 4',
    AuthorizedPerson5: 'Authorized Person 5',
    ApprovalSigner1: 'Approval Signer'
};

/** Convert a PascalCase/camelCase string to space-separated capitalized words */
function camelCaseToDisplayName(str: string): string {
    const spaced = str
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .replace(/([a-zA-Z])(\d)/g, '$1 $2')
        .replace(/(\d)([a-zA-Z])/g, '$1 $2');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Get the display name for a signature role. Uses explicit mapping if available, otherwise converts from camelCase. */
export function getSignatureRoleDisplayName(role: string): string {
    return ROLE_DISPLAY_NAMES[role] ?? camelCaseToDisplayName(role);
}

/**
 * Canonical role families the backend emits with an unbounded index rather than a fixed name,
 * e.g. `AdditionalAdvisor3` / `AuthorizedPerson6` (lib-orders getSignaturesPreview). These cannot
 * all be enumerated in ROLE_DISPLAY_NAMES, so they are recognized by shape. Namespaced custom /
 * principal keys always carry a `:` and so never match these bare patterns.
 */
const DYNAMIC_CANONICAL_ROLE_PATTERNS: readonly RegExp[] = [
    /^AdditionalAdvisor\d+$/,
    /^AuthorizedPerson\d+$/
];

/**
 * Whether a preview group key is a canonical signer role. The backend merges matching custom
 * signers into a canonical group (e.g. a custom signer folded into `AdditionalAdvisor1`), so a
 * canonical key can hold signatures whose own `role` is `Custom`. Callers use this to let the
 * canonical key win over the signature's role when deriving the display label. A key is canonical
 * if it has an explicit display-name mapping or matches a dynamic canonical family. Namespaced keys
 * (`Custom:…`, `MarketplacePrincipal:…`) are never canonical.
 */
export function isCanonicalRoleKey(key: string): boolean {
    if (Object.prototype.hasOwnProperty.call(ROLE_DISPLAY_NAMES, key)) {
        return true;
    }
    return DYNAMIC_CANONICAL_ROLE_PATTERNS.some(pattern => pattern.test(key));
}

/**
 * Get the sidebar display name for a signature-preview group.
 *
 * A custom signer is namespaced (`Custom: <roleName>`) only when its roleName matches a real
 * role that is actually present with valid signatures in the current order — otherwise there is
 * nothing to disambiguate from and the roleName alone is shown. Post-advisor principals follow
 * the backend's namespaced key (`MarketplacePrincipal:Advisor` -> `Principal: Advisor`).
 *
 * @param groupKey - the preview group key (may be namespaced)
 * @param role - the signature's base role (e.g. 'Custom', 'MarketplacePrincipal', 'Investor')
 * @param roleName - the signature's roleName, when present
 * @param rolesWithValidSignatures - base roles that have valid signatures in this order's preview,
 *   used to decide whether a custom signer's roleName actually collides with a displayed role
 */
export function getSignatureGroupDisplayName(
    groupKey: string,
    role: string,
    roleName?: string,
    rolesWithValidSignatures?: ReadonlySet<string>
): string {
    if (role === 'Custom') {
        const customRoleName = roleName ?? groupKey.replace(/^Custom:/, '');
        // Namespace only on a real overlap: the roleName must match a role that is present
        // with valid signatures in this order. No overlap -> no namespacing.
        const collidesWithRealRole =
            !!customRoleName && (rolesWithValidSignatures?.has(customRoleName) ?? false);
        return collidesWithRealRole
            ? `Custom: ${getSignatureRoleDisplayName(customRoleName)}`
            : getSignatureRoleDisplayName(customRoleName);
    }
    if (role === 'MarketplacePrincipal' && groupKey.toLowerCase() !== role.toLowerCase()) {
        const prefix = `${role}:`;
        if (groupKey.startsWith(prefix)) {
            // Backend emits the 'MarketplacePrincipal:<role>' colon-key only when the signer's roleName
            // clashes with a real role. Flag it with the raw token prefix — the same red-flag treatment
            // a clashing Custom signer gets — rather than the friendly 'Principal:' label.
            const clashingName = roleName || groupKey.slice(prefix.length);
            return `${role}: ${getSignatureRoleDisplayName(clashingName)}`;
        }
        // A plainly named principal: the bare key is the signer's name → friendly 'Principal: <name>'.
        return `Principal: ${getSignatureRoleDisplayName(groupKey)}`;
    }
    // A generic principal (bare role token, no distinguishing name) → canonical 'Principal'.
    return getSignatureRoleDisplayName(role);
}

/** The resolved identity for a preview group: the role the label is derived from and the label itself. */
export interface SignatureGroupLabel {
    role: string;
    displayName: string;
}

/**
 * Build the canonical `groupKey -> { role, displayName }` map for a signatures-preview response.
 *
 * This is the single source of truth for how a preview group is labelled. Both the sidebar
 * (order-document-viewer) and the PDF overlay (aix-signature-overlay) key off `groupKey` for color,
 * hide/show, and wet-sign state — and must label off the same identity so the two views never
 * disagree (e.g. sidebar "Custom: Advisor" vs overlay "Advisor").
 *
 * The group KEY is the backend's identity. The signature role is only deferred to for the prefixed
 * types (`Custom` / `MarketplacePrincipal`) when the key is not already canonical — a canonical key
 * (e.g. `AdditionalAdvisor1` holding a merged custom signature) must win.
 */
export function getSignatureGroupLabels(
    preview: SignaturesPreviewResponse
): Map<string, SignatureGroupLabel> {
    // Canonical roles actually displayed with valid signatures, keyed off the canonical group key
    // (its identity) — used to decide whether a custom signer's roleName collides with a real role.
    const rolesWithValidSignatures = new Set<string>();
    for (const [key, group] of Object.entries(preview)) {
        if (group.signatures.length > 0 && isCanonicalRoleKey(key)) {
            rolesWithValidSignatures.add(key);
        }
    }

    const labels = new Map<string, SignatureGroupLabel>();
    for (const [key, group] of Object.entries(preview)) {
        const signatureRole = group.signatures[0]?.role;
        const role =
            (signatureRole === 'Custom' || signatureRole === 'MarketplacePrincipal') &&
            !isCanonicalRoleKey(key)
                ? signatureRole
                : key;
        const displayName = getSignatureGroupDisplayName(
            key,
            role,
            group.signatures[0]?.roleName,
            rolesWithValidSignatures
        );
        labels.set(key, { role, displayName });
    }
    return labels;
}
